import {
  Row, Col, Card, Table, Button, Input, DatePicker, Tooltip,
  Avatar, Tabs, Switch, Skeleton, Badge
} from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import moment from 'moment';
import { formatDate, formatDateTime } from "../../helper/DateHelper";
import { useNavigate, useLocation, Link } from "react-router-dom";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import From from "./From";
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import ConfirmationBox from "../../components/ConfirmationBox";
import ImportForm from "../../components/ImportForm";
import { getSortOrder } from "../../constants/conts";

const { TabPane } = Tabs;
const Search = Input.Search;
const { RangePicker } = DatePicker;

function Index() {
  const sectionName = "User";
  const routeName = "user";

  const api = {
    status: apiPath.statusUser,
    addEdit: apiPath.addEditUser,
    list: apiPath.listUser,
    importFile: apiPath.importFile,
  };

  const { request } = useRequest();
  const { showConfirm } = ConfirmationBox();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  // ---- parse initial values from query string ----
  const initialPage = parseInt(query.get("page")) || 1;
  const initialPageSize = parseInt(query.get("pageSize")) || 20;
  const initialSearch = query.get("search") || "";
  const initialStartDate = query.get("startDate") || undefined;
  const initialEndDate = query.get("endDate") || undefined;
  const initialOtpVerification = query.get("tab") === "true";
  const initialSort = query.get("sort") || "";

  // ---- state (use initial values) ----
  const [searchText, setSearchText] = useState(initialSearch);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState();
  const [importVisible, setImportVisible] = useState(false);

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [sortBy, setSortBy] = useState(initialSort);
  const [otpVerification, setOtpVerification] = useState(initialOtpVerification);

  const [pagination, setPagination] = useState({
    current: initialPage,
    pageSize: initialPageSize,
  });

  const [filters, setFilters] = useState({});
  const debouncedSearchText = useDebounce(searchText, 500);

  // ---- navigate to view while preserving query string ----
  const view = (id) => {
    navigate(`/${routeName}/view/${id}${location.search}`);
  };

  // ---- sync state -> query string (so back/forward restores state) ----
  useEffect(() => {
    const params = new URLSearchParams();

    if (pagination.current > 1) params.set("page", pagination.current);
    if (pagination.pageSize && pagination.pageSize !== 20) params.set("pageSize", pagination.pageSize);
    if (debouncedSearchText) params.set("search", debouncedSearchText);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    if (sortBy) params.set("sort", sortBy);
    params.set("tab", otpVerification);

    // replace so browser back works naturally
    navigate({ search: params.toString() }, { replace: true });
  }, [
    pagination.current,
    pagination.pageSize,
    debouncedSearchText,
    startDate,
    endDate,
    otpVerification,
    sortBy,
    navigate,
  ]);

  // ---- fetch data whenever relevant state changes ----
  useEffect(() => {
    setLoading(true);
    fetchData(pagination, filters, otpVerification, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    refresh,
    debouncedSearchText,
    otpVerification,
    sortBy,
    pagination.current,
    pagination.pageSize,
    startDate,
    endDate,
    filters,
  ]);

  const fetchData = (pageInfo, filters, otpVerificationFlag, sortValue = '') => {
    const filterActive = filters ? filters.is_active : null;

    request({
      url:
        api.list +
        `?status=${filterActive ? filterActive.join(',') : ''}` +
        `&page=${pageInfo.current}` +
        `&limit=${pageInfo.pageSize}` +
        `&search=${debouncedSearchText}` +
        `&otp_verification=${otpVerificationFlag}` +
        `&start_date=${startDate || ""}` +
        `&end_date=${endDate || ""}` +
        `&sort=${sortValue || ''}`,
      method: 'GET',
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs || []);
        setPagination(prev => ({
          ...prev,
          current: pageInfo.current,
          pageSize: pageInfo.pageSize,
          total: data.data.list.totalDocs,
        }));
      },
      onError: (error) => {
        console.log(error);
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      }
    });
  };

  // ---- table change handler: update state only (no direct fetch) ----
  const handleChange = (pag, newFilters, sorter) => {
    const order = sorter?.order;
    const field = sorter?.field;

    let nextSort = '';
    if (order) {
      nextSort = `${field}:${order === 'ascend' ? 'asc' : 'desc'}`;
    }

    setFilters(newFilters || {});
    setSortBy(nextSort);

    setPagination(prev => ({
      current: pag?.current || 1,
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value);
    // reset to first page on new search
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleChangeDate = (dates) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    if (dates && dates.length === 2) {
      setStartDate(moment(dates[0]).format("YYYY-MM-DD"));
      setEndDate(moment(dates[1]).format("YYYY-MM-DD"));
    } else {
      setStartDate(undefined);
      setEndDate(undefined);
    }
  };

  const handleTabChange = (key) => {
    // key is a string: "true" or "false"
    const isOtpVerified = key === "true";
    setOtpVerification(isOtpVerified);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // ---- columns: include Edit & View (View preserves query string) ----
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
      sorter: true,
      sortOrder: getSortOrder(sortBy, "name"),
      
      render: (_, { name, _id }) => {
        const castName = name || 'Anonymous User';
        return (
          <>
            <Avatar className="cap" size={40}>{castName.charAt(0)}</Avatar>
            <a style={{ marginLeft: 12 }} className="cap avatar-text" onClick={() => view(_id)}>{castName}</a>
          </>
        );
      },
    },
    {
      title: "Mobile Number",
      width: 200,
      dataIndex: "mobile_number",
      key: "mobile_number",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "mobile_number"),
      render: (_, { mobile_number, country_code }) => ((country_code ? "+" + country_code + "-" : "+91") + (mobile_number || '')),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      width: 250,
      sorter: true,
      sortOrder: getSortOrder(sortBy, "email"),
      render: (_, { email }) => email || '-',
    },
    {
      title: "DOB",
      dataIndex: "dob",
      key: "dob",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "dob"),
      render: (_, { dob }) => dob || '-',
    },
    {
      title: "Status",
      key: "is_active",
      ...(otpVerification && { filters: [{ text: 'Active', value: true }, { text: 'Inactive', value: false }] }),
      render: (_, { is_active, _id, is_otp_verify }) => (
        is_otp_verify ? (
          <Switch
            checked={is_active}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            loading={loading}
            onChange={(checked) =>
              showConfirm({
                record: _id,
                path: api.status,
                onLoading: () => setLoading(true),
                onSuccess: () => setRefresh(prev => !prev),
                is_active: checked,
              })
            }
            className={is_active ? "custom-switch-checked" : ""}
          />
        ) : <span className="text-danger">OTP not verified</span>
      ),
    },
    {
      title: "Added SP Bucket",
      dataIndex: "sp_count",
      key: "sp_count",
      render: (_, { sp_count, _id }) => {
        const btnClass = sp_count > 0 ? 'ant-btn-primary' : 'ant-btn-secondary';
        return <Link className={`ant-btn ${btnClass}`} to={`/${routeName}/service-product/${_id}${location.search}`}>{sp_count || 0}</Link>;
      },
    },
    {
      title: "Registered On",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (_, { created_at }) => formatDate(created_at),
    },
    {
      title: "Action",
      fixed: 'right',
      render: (_, record) => (
        <>
          <Tooltip title={`Update ${sectionName}`} color="purple">
            <Button
              onClick={() => { setSelected(record); setVisible(true); }}
            >
              <i className="fa fa-light fa-pen"></i>
            </Button>
          </Tooltip>

          <Tooltip title={`View ${sectionName}`} color="purple">
            <Button onClick={() => view(record._id)}>
              <i className="fa fa-light fa-eye"></i>
            </Button>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="tabled customerMain">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title={sectionName + " Manager"}
              extra={
                <>
                  <div className="searchOuter">
                    <Search
                      className="mainSearch"
                      size="large"
                      allowClear
                      onChange={onSearch}
                      value={searchText}
                      onPressEnter={onSearch}
                      placeholder="Search By Name, Mobile, Email"
                    />
                    <RangePicker
                      value={startDate && endDate ? [moment(startDate), moment(endDate)] : undefined}
                      onChange={handleChangeDate}
                    />
                  </div>

                  <div className="button_group">
                    <Button title="Import" onClick={() => { setImportVisible(true); setSearchText(''); }}>
                      <i className="fas fa-cloud-upload-alt"></i>&nbsp;&nbsp;Import
                    </Button>
                    <Button onClick={() => { setVisible(true); setSearchText(''); }}>Add New {sectionName}</Button>
                  </div>
                </>
              }
            >
              <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>

              <Tabs
                activeKey={String(otpVerification)}
                onChange={handleTabChange}
                className="min-font-bold"
                tabBarStyle={{ color: "Black" }}
              >
                <TabPane tab="Non Verified" key="false">
                  <div className="table-responsive customPagination">
                    <Table
                      loading={loading}
                      columns={columns}
                      dataSource={list}
                      rowKey={record => record._id}
                      pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ["10", "20", "30", "50", "100"],
                      }}
                      onChange={handleChange}
                      className="ant-border-space"
                    />
                  </div>
                </TabPane>

                <TabPane tab="OTP Verified" key="true">
                  <div className="table-responsive customPagination">
                    <Table
                      loading={loading}
                      columns={columns}
                      dataSource={list}
                      rowKey={record => record._id}
                      pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ["10", "20", "30", "50", "100"],
                      }}
                      onChange={handleChange}
                      className="ant-border-space"
                    />
                  </div>
                </TabPane>
              </Tabs>
            </Card>
          </Col>
        </Row>
      </div>

      {visible && (
        <From
          path={api.addEdit}
          sectionName={sectionName}
          show={visible}
          hide={() => { setSelected(); setVisible(false); }}
          data={selected}
          refresh={() => {
            setRefresh(prev => !prev)
          }}
        />
      )}

      {importVisible && (
        <ImportForm
          path={api.importFile}
          sectionName={sectionName}
          show={importVisible}
          hide={() => setImportVisible(false)}
          existingData={list}
          refresh={() => {
            setRefresh(prev => !prev)
          }}
        />
      )}
    </>
  );
}

export default Index;
